import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConfigurationService, PermissionDto, RoleDetailDto } from '../configuration.service';
import { AuthService } from '../../services/auth.service';

export interface RoleEditDialogData {
  roleId: number | null;
}

interface PermissionGroup {
  category: string;
  permissions: PermissionDto[];
}

@Component({
  selector: 'app-role-edit',
  templateUrl: './role-edit.component.html',
  styleUrls: ['../configuration-form.css', './role-edit.component.css'],
})
export class RoleEditComponent implements OnInit {
  form: FormGroup;
  mode: 'create' | 'edit' = 'create';
  roleId: number | null = null;
  roleDetail: RoleDetailDto | null = null;
  permissionGroups: PermissionGroup[] = [];
  loading = true;
  submitting = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<RoleEditComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) private data: RoleEditDialogData,
    private api: ConfigurationService,
    private snackBar: MatSnackBar,
    public auth: AuthService
  ) {
    this.form = this.fb.group({
      name:           ['', [Validators.required, Validators.maxLength(64)]],
      description:    ['', Validators.maxLength(255)],
      permissionKeys: [[]]
    });
  }

  ngOnInit(): void {
    this.mode = this.data.roleId ? 'edit' : 'create';
    if (this.mode === 'edit') this.roleId = this.data.roleId;

    this.api.getPermissions().subscribe({ next: perms => this.buildGroups(perms) });

    if (this.mode === 'edit' && this.roleId) {
      this.api.getRoleById(this.roleId).subscribe({
        next: role => {
          this.roleDetail = role;
          this.form.patchValue({ name: role.name, description: role.description || '', permissionKeys: [...role.permissionKeys] });
          if (!this.auth.hasPermission('Role.Manage')) this.form.disable();
          this.loading = false;
        },
        error: () => { this.loading = false; this.snackBar.open('Failed to load role', undefined, { duration: 4000 }); }
      });
    } else {
      this.loading = false;
    }
  }

  private buildGroups(perms: PermissionDto[]): void {
    const map = new Map<string, PermissionDto[]>();
    perms.forEach(p => {
      const cat = p.category || 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(p);
    });
    this.permissionGroups = Array.from(map.entries()).map(([category, permissions]) => ({ category, permissions }));
  }

  getKeys(): string[] { return this.form.get('permissionKeys')?.value ?? []; }

  isChecked(key: string): boolean { return this.getKeys().includes(key); }

  toggleKey(key: string): void {
    const keys: string[] = [...this.getKeys()];
    const idx = keys.indexOf(key);
    if (idx >= 0) keys.splice(idx, 1); else keys.push(key);
    this.form.get('permissionKeys')!.setValue(keys);
  }

  categoryCheckedCount(group: PermissionGroup): number {
    return group.permissions.filter(p => this.isChecked(p.key)).length;
  }

  categoryIndeterminate(group: PermissionGroup): boolean {
    const c = this.categoryCheckedCount(group);
    return c > 0 && c < group.permissions.length;
  }

  categoryAllChecked(group: PermissionGroup): boolean {
    return this.categoryCheckedCount(group) === group.permissions.length;
  }

  toggleCategory(group: PermissionGroup, checked: boolean): void {
    const keys: string[] = [...this.getKeys()];
    group.permissions.forEach(p => {
      const idx = keys.indexOf(p.key);
      if (checked && idx < 0) keys.push(p.key);
      else if (!checked && idx >= 0) keys.splice(idx, 1);
    });
    this.form.get('permissionKeys')!.setValue(keys);
  }

  save(): void {
    if (this.form.invalid || this.submitting) return;
    this.submitting = true;
    const v = this.form.value;
    const dto = { name: v.name, description: v.description || null, permissionKeys: v.permissionKeys };
    const req = this.mode === 'create' ? this.api.createRole(dto) : this.api.updateRole(this.roleId!, dto);
    req.subscribe({
      next: () => { this.snackBar.open('Role saved', undefined, { duration: 3000 }); this.dialogRef.close(true); },
      error: err => {
        this.submitting = false;
        if (err.status === 409) {
          this.form.get('name')!.setErrors({ nameConflict: true });
        } else {
          this.snackBar.open('Failed to save role', undefined, { duration: 4000 });
        }
      }
    });
  }

  cancel(): void {
    if (this.form.dirty && !confirm('Discard unsaved changes?')) return;
    this.dialogRef.close(false);
  }
}
